import React, { useEffect } from "react";
import {
  ArcMenuItem,
  ArcOutlinedInput,
  ArcOutlinedSelect,
  ArcStack,
  ArcText,
} from "@src/core/ui";
import { Controller } from "react-hook-form";
import { ArcGoogleSearch } from "@src/core/components";
import { selectMapLocation, setMapLocation } from "@src/core/controllers";
import { useDispatch, useSelector } from "react-redux";
import { REQUIRED_VALIDATION_TEXT } from "@src/core/config";
import { useChangeCoords } from "@src/core/hooks/useChangeCoords";
import { InputAdornment } from "@mui/material";
import {
  selectZoneMapLocationUponCreation,
  setZoneMapLocationUponCreation,
} from "@src/features/zones/redux";
import { ZoneType } from "@src/core/features";
import { ZoneTypeName } from "@src/features/zones/config/zones-constants";
import { VARIANT_SAFE_LAND } from "@src/features/zones/pages/zones-page/types";
import { SafeLandZoneFormProps } from "./types";

const MAX_ALTITUDE_M = 10000;

export const SafeLandZoneForm: React.FC<SafeLandZoneFormProps> = ({
  control,
  setValue,
  register,
  formState: { errors },
  watch,
}) => {
  const dispatch = useDispatch();
  const onChangeCoords = useChangeCoords();
  const location = useSelector(selectMapLocation);
  const createdZoneUponCreation = useSelector(selectZoneMapLocationUponCreation);

  const updateZoneMapLocation = (_lat: number, _lng: number, zoneName: string) => {
    dispatch(
      setZoneMapLocationUponCreation({
        id: watch("id") ?? 0,
        latitude: _lat,
        longitude: _lng,
        zoneName,
        radius: 0,
        type: watch("zoneType") ?? ZoneType.TEMPORARY,
        variant: VARIANT_SAFE_LAND,
      })
    );
  };

  useEffect(() => {
    if (location) {
      if (location.address) setValue("address", location.address);
      setValue("latitude", String(location.latitude));
      setValue("longitude", String(location.longitude));
      if (createdZoneUponCreation) {
        dispatch(
          setZoneMapLocationUponCreation({
            ...createdZoneUponCreation,
            latitude: location.latitude,
            longitude: location.longitude,
          })
        );
      }
    }
    // Intentionally omit createdZoneUponCreation: we only react to location changes.
    // Including it would cause an infinite loop (effect dispatches, which updates createdZoneUponCreation, which re-runs the effect).
  }, [location, setValue, dispatch]);

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcStack marginTop={3} spacing={2}>

          <Controller
            control={control}
            name="address"
            rules={{ required: "This field is required" }}
            render={({ field: { value } }) => (
              <ArcGoogleSearch
                initValue={value}
                label="Zone Location"
                error={Boolean(errors.address)}
                helperText={errors.address?.message}
                onSelect={(lat, lng, address) => {
                  setValue("address", address);
                  setValue("latitude", String(lat));
                  setValue("longitude", String(lng));
                  dispatch(setMapLocation({ latitude: lat, longitude: lng, address }));
                  updateZoneMapLocation(lat, lng, watch("zoneName") ?? "");
                }}
              />
            )}
          />

          <ArcStack direction="row" spacing={2}>
            <Controller
              name="latitude"
              control={control}
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field }) => (
                <ArcOutlinedInput
                  {...field}
                  label="Latitude"
                  autoComplete="off"
                  error={Boolean(errors.latitude)}
                  helperText={errors.latitude?.message}
                  onChange={(e) => {
                    const lat = Number(e.target.value);
                    setValue("latitude", String(lat));
                    onChangeCoords({ lat, lng: Number(watch("longitude") ?? 0) });
                    updateZoneMapLocation(lat, +(watch("longitude") ?? 0), watch("zoneName") ?? "");
                  }}
                />
              )}
            />
            <Controller
              name="longitude"
              control={control}
              rules={{ required: REQUIRED_VALIDATION_TEXT }}
              render={({ field }) => (
                <ArcOutlinedInput
                  {...field}
                  label="Longitude"
                  autoComplete="off"
                  error={Boolean(errors.longitude)}
                  helperText={errors.longitude?.message}
                  onChange={(e) => {
                    const lng = Number(e.target.value);
                    setValue("longitude", String(lng));
                    onChangeCoords({ lat: Number(watch("latitude") ?? 0), lng });
                    updateZoneMapLocation(+(watch("latitude") ?? 0), lng, watch("zoneName") ?? "");
                  }}
                />
              )}
            />
          </ArcStack>

          <Controller
            name="zoneName"
            control={control}
            rules={{
              required: "Zone name is required",
              maxLength: { value: 20, message: "Maximum 20 characters allowed" },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <ArcOutlinedInput
                autoComplete="off"
                label="Zone Name"
                value={value || ""}
                error={Boolean(error)}
                helperText={error?.message}
                maxLength={20}
                onChange={(e) => {
                  const val = e.target.value;
                  onChange(val);
                  setValue("zoneName", val);
                  updateZoneMapLocation(
                    +(watch("latitude") ?? 0),
                    +(watch("longitude") ?? 0),
                    val
                  );
                }}
              />
            )}
          />

          <Controller
            name="zoneDescription"
            control={control}
            rules={{
              maxLength: { value: 200, message: "Maximum 200 characters allowed" },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <ArcOutlinedInput
                autoComplete="off"
                label="Zone Description"
                value={value || ""}
                error={Boolean(error)}
                helperText={error?.message}
                maxLength={200}
                multiline
                onChange={onChange}
              />
            )}
          />

          <ArcOutlinedInput
            type="number"
            autoComplete="off"
            label="Zone Altitude"
            error={
              Boolean(errors.zoneAltitude) ||
              Number(watch("zoneAltitude") ?? 0) > MAX_ALTITUDE_M
            }
            helperText={
              errors.zoneAltitude?.message ||
              (Number(watch("zoneAltitude") ?? 0) > MAX_ALTITUDE_M
                ? `Maximum altitude is ${MAX_ALTITUDE_M} m`
                : "")
            }
            {...register("zoneAltitude", {
              required: REQUIRED_VALIDATION_TEXT,
              validate: (value) =>
                Number(value) <= MAX_ALTITUDE_M || `Maximum altitude is ${MAX_ALTITUDE_M} m`,
            })}
            onChange={(e) => {
              const numericValue = Number(e.target.value);
              setValue("zoneAltitude", numericValue, { shouldValidate: true });
              updateZoneMapLocation(
                +(watch("latitude") ?? 0),
                +(watch("longitude") ?? 0),
                watch("zoneName") ?? ""
              );
            }}
            value={watch("zoneAltitude") ?? ""}
            inputProps={{ step: 0.1, min: 0 }}
            InputLabelProps={{ shrink: true }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <ArcText.h5>m</ArcText.h5>
                  </InputAdornment>
                ),
              },
            }}
          />
        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};
